import { Input, Switch } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import {
  defaultRangeRepeat,
  rangeRepeatPreview,
  type RangeRepeat,
} from '@/lib/domain/rangeRepeat';

export interface RangeRepeatFieldsProps {
  /** null = 반복 없음. */
  value: RangeRepeat | null;
  onChange: (next: RangeRepeat | null) => void;
  /** 측정 단위 — 해외 kg, 국내 BY_AMOUNT 원 / BY_QTY 개 / BY_WEIGHT kg. */
  unitLabel: string;
  /** 첫 구간(가장 낮은 구간) 요금. 정액이 아니면 null — 미리보기를 생략한다. */
  baseFee: number | null;
  /** 첫 구간이 끝나는 값 — 켤 때 시작값 기본으로 쓴다. */
  firstUpperBound: number | null;
  /** 구간이 1개 이상 있는지. 없으면 켤 수는 있어도 저장이 막힌다(BE 400) — 그 사실을 알려준다. */
  hasTiers: boolean;
  disabled: boolean;
  /** 같은 화면에 여러 번 나올 때(국가그룹마다) 입력란을 구분하는 접두어. */
  labelPrefix?: string;
}

const toNumber = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 배송비 "범위반복" 입력 — 해외 국가그룹과 국내 배송비조건이 함께 쓴다.
 * 규칙·검증·미리보기 계산은 `lib/domain/rangeRepeat` 에 있고, 여기서는 입력과 표시만 한다.
 */
export const RangeRepeatFields = ({
  value,
  onChange,
  unitLabel,
  baseFee,
  firstUpperBound,
  hasTiers,
  disabled,
  labelPrefix = '',
}: RangeRepeatFieldsProps) => {
  const { t } = useTranslation('common');
  const enabled = value !== null;
  const label = (key: string, fallback: string) =>
    `${labelPrefix}${t(`rangeRepeat.${key}`, { defaultValue: fallback })}`;

  const preview = enabled && baseFee !== null ? rangeRepeatPreview(baseFee, value, unitLabel) : '';

  return (
    <div className="flex flex-col gap-2 rounded-md p-2" style={{ background: 'var(--surface-0)' }}>
      <label className="flex items-center gap-2 text-[12px] font-medium" style={{ color: 'var(--content-strong)' }}>
        <Switch
          checked={enabled}
          onCheckedChange={(on) => onChange(on ? defaultRangeRepeat(firstUpperBound) : null)}
          disabled={disabled}
          aria-label={label('toggle', '범위반복 사용')}
        />
        {t('rangeRepeat.toggle', { defaultValue: '범위반복 사용' })}
      </label>

      {enabled ? (
        <>
          <div className="flex flex-wrap items-center gap-3 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            <span className="flex items-center gap-1">
              {t('rangeRepeat.start', { defaultValue: '시작' })}
              <Input
                type="number"
                min={0}
                step="any"
                value={String(value.start)}
                onChange={(e) => onChange({ ...value, start: toNumber(e.target.value) })}
                disabled={disabled}
                className="w-[96px] tabular-nums"
                aria-label={label('start', '시작')}
              />
              {unitLabel}
            </span>
            <span className="flex items-center gap-1">
              {t('rangeRepeat.unit', { defaultValue: '단위' })}
              <Input
                type="number"
                min={0}
                step="any"
                value={String(value.unit)}
                onChange={(e) => onChange({ ...value, unit: toNumber(e.target.value) })}
                disabled={disabled}
                className="w-[96px] tabular-nums"
                aria-label={label('unit', '단위')}
              />
              {unitLabel}
            </span>
            <span className="flex items-center gap-1">
              {t('rangeRepeat.fee', { defaultValue: '추가요금' })}
              <Input
                type="number"
                min={0}
                step={1}
                value={String(value.fee)}
                onChange={(e) => onChange({ ...value, fee: toNumber(e.target.value) })}
                disabled={disabled}
                className="w-[120px] tabular-nums"
                aria-label={label('fee', '추가요금')}
              />
              {t('rangeRepeat.won', { defaultValue: '원' })}
            </span>
          </div>

          {/* 반복을 켜면 BE 는 첫 구간만 쓴다 — 나머지 구간을 고쳐도 요금이 안 바뀌는 이유를 여기서 알린다. */}
          <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
            {t('rangeRepeat.firstTierOnly', {
              defaultValue: '범위반복을 쓰면 첫 구간만 적용됩니다. 두 번째 이후 구간은 계산에 쓰이지 않습니다.',
            })}
          </p>

          {!hasTiers ? (
            <p className="text-[11px]" style={{ color: 'var(--status-danger-fg)' }}>
              {t('rangeRepeat.tierRequired', {
                defaultValue: '범위반복을 쓰려면 구간이 1개 이상 있어야 합니다. 첫 구간 요금이 기준 요금입니다.',
              })}
            </p>
          ) : null}

          {preview ? (
            <p className="text-[12px] tabular-nums" style={{ color: 'var(--content-default)' }} data-testid="range-repeat-preview">
              {preview}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
